import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { fetchCart } from "../../../../store/cart/cartReducer";
import { createOrder } from "../../../../store/orders/orderReducer";
import { createPaymentIntent, resetPayment } from "../../../../store/payments/paymentReducer";
import Spinner from "../../../../components/Spinner/Index";

// Load Stripe instance
const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY || "");

const CheckoutPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();

    const token = useSelector((state) => state.auth.token);
    const { cart = {}, isLoading: isCartLoading, isError: isCartError } = useSelector((state) => state.cart);
    const { clientSecret, isLoading: isPaymentLoading } = useSelector((state) => state.payment);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cardComplete, setCardComplete] = useState(false); // Track card validity

    const { items = [], totalPrice } = {
        ...cart,
        totalPrice: cart.items?.reduce(
            (total, item) => total + item.packagePrice * item.quantity,
            0
        ),
    };


    // Fetch cart and create payment intent
    useEffect(() => {
        if (token) {
            dispatch(fetchCart(token)); // Fetch cart items
        }

        if (totalPrice > 0 && token) {
            dispatch(createPaymentIntent({ amount: totalPrice * 100, token })); // Create payment intent
        }

        return () => {
            dispatch(resetPayment()); // Reset payment state on unmount
        };
    }, [dispatch, token, totalPrice]);

    // Handle card input change
    const handleCardChange = (event) => {
        setCardComplete(event.complete); // Update card validity
    };

    const handleCheckout = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            alert("Stripe is not properly initialized.");
            return;
        }

        try {
            setIsSubmitting(true);

            if (!clientSecret) {
                throw new Error("ClientSecret is null. Ensure createPaymentIntent is working correctly.");
            }

            const cardElement = elements.getElement(CardElement);
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: "Customer Name",
                    },
                },
            });

            if (result.error) {
                throw new Error(result.error.message);
            }

            const orderData = {
                cartId: cart._id, // Ensure cart._id exists
                paymentIntentId: result.paymentIntent.id, // Ensure this is returned correctly
            };

            const orderResponse = await dispatch(createOrder(orderData)).unwrap();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "purchase",
                ecommerce: {
                    transaction_id: result.paymentIntent.id,
                    value: totalPrice,
                    currency: "USD",
                    payment_type: "card",
                    items: items.map(item => ({
                        item_name: item.packageName,
                        item_id: item._id,
                        price: item.packagePrice,
                        quantity: item.quantity,
                        business_name: item.businessId.businessInformation.businessName,
                    })),
                },
            });

            navigate(`/user/orders/${orderResponse.orderId}`);
        } catch (error) {
            console.error("Checkout failed:", error.message);
            alert("Checkout failed: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isCartLoading || isPaymentLoading) return <Spinner />;

    if (isCartError || !items.length) {
        return (
            <div className="p-6 text-center">
                <h2 className="text-xl font-bold text-gray-800">Your cart is empty!</h2>
                <button
                    onClick={() => navigate("/user/shop")}
                    className="mt-4 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                    Continue Shopping
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white">
            <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
                <h1 className="text-center text-3xl font-bold text-gray-900">
                    Checkout
                </h1>

                <div className="mt-6">
                    <ul role="list" className="divide-y divide-gray-200">
                        {items.map((item) => (
                            <li
                                key={item._id}
                                className="flex items-center justify-between border-b border-gray-200 py-6"
                            >
                                {/* Product Image */}
                                <div className="flex items-center">
                                    <img
                                        src={`/public/${item.product.image_url}`}
                                        alt={item.product.name}
                                        className="h-24 w-24 rounded-md object-cover sm:h-32 sm:w-32"
                                    />
                                    <div className="ml-4">
                                        {/* Package Name */}
                                        <h3 className="text-base font-semibold text-gray-900">
                                            {item.packageName}
                                        </h3>
                                        {/* Product Name */}
                                        <p className="mt-1 text-sm text-gray-500">{item.product.name}</p>
                                        {/* Business Name */}
                                        <p className="mt-1 text-sm text-gray-500">
                                            For Business:{" "}
                                            <span className="font-medium text-gray-700">
                                                {item.businessId.businessInformation.businessName}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Pricing and Actions */}
                                <div className="flex flex-col items-end">
                                    {/* Total Price */}
                                    <p className="text-lg font-medium text-gray-900">
                                        ${item.packagePrice * item.quantity}
                                    </p>
                                    {/* Quantity */}
                                    <p className="mt-1 text-sm text-gray-500">Qty: {item.quantity}</p>
                                    {/* Remove Button */}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveItem(item._id, item.businessId)}
                                        className="mt-2 text-sm text-red-500 hover:underline"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </li>
                        ))}

                    </ul>

                    <div className="mt-6 border-t border-gray-200 pt-6">
                        <h2 className="text-lg font-bold text-gray-900">
                            Payment Details
                        </h2>
                        <form onSubmit={handleCheckout} className="mt-4">
                            <div className="rounded-md border border-gray-300 p-4">
                                <CardElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: "16px",
                                                color: "#424770",
                                                "::placeholder": { color: "#aab7c4" },
                                            },
                                            invalid: { color: "#9e2146" },
                                        },
                                    }}
                                    onChange={handleCardChange} // Track card input changes
                                />
                            </div>
                            <div className="mt-6 flex justify-between">
                                <p className="text-lg font-medium text-gray-900">
                                    Total: ${totalPrice.toFixed(2)}
                                </p>
                                <button
                                    type="submit"
                                    className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-white text-base font-medium shadow-sm focus:outline-none ${isSubmitting || !stripe || !cardComplete
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700"
                                        }`}
                                    disabled={!stripe || !cardComplete || isSubmitting}
                                >
                                    {isSubmitting ? "Processing..." : "Pay Now"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default () => (
    <Elements stripe={stripePromise}>
        <CheckoutPage />
    </Elements>
);